import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
    Clock, PencilSimple, FileCsv, FileXls, Plus, Eye, ClockCounterClockwise, Warning, LockKey,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { LocationCell, TimeRecordDetailDialog } from "@/components/TimeRecordDetail";
import { CloseShiftDialog } from "@/components/CloseShiftDialog";
import { formatClockTime, formatWorkDate, formatSeconds, formatAuditValue, COMPANY_TZ_LABEL } from "@/lib/time";

const ALL = "all";
const EMPTY_NEW = {
    technician_id: "", site_id: "", sublocation_id: "",
    clock_in_local: "", clock_out_local: "", notes: "", reason: "",
};

export default function AdminTimeRecords() {
    const [entries, setEntries] = useState([]);
    const [techs, setTechs] = useState([]);
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshTick, setRefreshTick] = useState(0);

    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [techFilter, setTechFilter] = useState(ALL);
    const [siteFilter, setSiteFilter] = useState(ALL);
    const [subFilter, setSubFilter] = useState(ALL);
    const [statusFilter, setStatusFilter] = useState(ALL);
    const [filterSubs, setFilterSubs] = useState([]);

    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [editSubs, setEditSubs] = useState([]);
    const [saving, setSaving] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [newForm, setNewForm] = useState(EMPTY_NEW);
    const [newSubs, setNewSubs] = useState([]);

    const [auditEntry, setAuditEntry] = useState(null);
    const [detailEntry, setDetailEntry] = useState(null);
    const [closingEntry, setClosingEntry] = useState(null);

    const reload = useCallback(() => setRefreshTick((t) => t + 1), []);

    const queryString = useMemo(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set("date_from", dateFrom);
        if (dateTo) p.set("date_to", dateTo);
        if (techFilter !== ALL) p.set("technician_id", techFilter);
        if (siteFilter !== ALL) p.set("site_id", siteFilter);
        if (subFilter !== ALL) p.set("sublocation_id", subFilter);
        if (statusFilter !== ALL) p.set("status", statusFilter);
        return p.toString();
    }, [dateFrom, dateTo, techFilter, siteFilter, subFilter, statusFilter]);

    useEffect(() => {
        let cancelled = false;
        Promise.all([api.get("/users?role=technician"), api.get("/sites")])
            .then(([t, s]) => {
                if (cancelled) return;
                setTechs(Array.isArray(t.data) ? t.data : []);
                setSites(Array.isArray(s.data) ? s.data : []);
            })
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/time/entries${queryString ? `?${queryString}` : ""}`)
            .then(({ data }) => { if (!cancelled) setEntries(Array.isArray(data) ? data : []); })
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [queryString, refreshTick]);

    // Sub-location options for the site filter.
    useEffect(() => {
        if (siteFilter === ALL) { setFilterSubs([]); setSubFilter(ALL); return; }
        let cancelled = false;
        api.get(`/sites/${siteFilter}/sublocations`)
            .then(({ data }) => { if (!cancelled) setFilterSubs(Array.isArray(data) ? data : []); })
            .catch(() => {});
        setSubFilter(ALL);
        return () => { cancelled = true; };
    }, [siteFilter]);

    const loadSubs = async (siteId, setter) => {
        if (!siteId) { setter([]); return; }
        try {
            const { data } = await api.get(`/sites/${siteId}/sublocations`);
            setter(Array.isArray(data) ? data : []);
        } catch { setter([]); }
    };

    const openEdit = async (entry) => {
        setEditing(entry);
        setEditForm({
            site_id: entry.site_id,
            sublocation_id: entry.sublocation_id || "",
            clock_in_local: entry.clock_in_local || "",
            clock_out_local: entry.clock_out_local || "",
            notes: entry.notes || "",
            reason: "",
        });
        await loadSubs(entry.site_id, setEditSubs);
    };

    const siteById = (id) => sites.find((s) => s.id === id);

    const saveEdit = async () => {
        if (!editing) return;
        if ((editForm.reason || "").trim().length < 3) {
            toast.error("Please enter a correction reason");
            return;
        }
        setSaving(true);
        try {
            const body = {
                site_id: editForm.site_id,
                sublocation_id: editForm.sublocation_id || null,
                clock_in_local: editForm.clock_in_local,
                clock_out_local: editForm.clock_out_local || null,
                notes: editForm.notes,
                reason: editForm.reason,
            };
            await api.patch(`/time/entries/${editing.id}`, body);
            toast.success("Time record corrected — change logged in the audit trail");
            setEditing(null);
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const saveNew = async () => {
        if (!newForm.technician_id || !newForm.site_id || !newForm.clock_in_local) {
            toast.error("Employee, site and Clock In are required");
            return;
        }
        setSaving(true);
        try {
            await api.post("/time/entries", {
                ...newForm,
                sublocation_id: newForm.sublocation_id || null,
                clock_out_local: newForm.clock_out_local || null,
            });
            toast.success("Time record created");
            setCreateOpen(false);
            setNewForm(EMPTY_NEW);
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const download = async (format) => {
        try {
            const qs = queryString ? `&${queryString}` : "";
            const { data } = await api.get(`/time/entries/export?format=${format}${qs}`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `AdvanceNetworks_TimeRecords.${format}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 4000);
            toast.success(`${format.toUpperCase()} exported`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };


    const clearFilters = () => {
        setDateFrom(""); setDateTo(""); setTechFilter(ALL);
        setSiteFilter(ALL); setSubFilter(ALL); setStatusFilter(ALL);
    };
    const hasFilters = dateFrom || dateTo || techFilter !== ALL || siteFilter !== ALL
        || subFilter !== ALL || statusFilter !== ALL;

    return (
        <div className="space-y-6" data-testid="admin-time-records-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                        Admin · Attendance
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Time Records
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm max-w-2xl">
                        Actual Clock In / Clock Out attendance. Times shown in {COMPANY_TZ_LABEL}.
                        Elapsed time is informational only — no rounding or payroll math is applied.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        onClick={() => download("csv")}
                        className="h-10 border-[#003366] text-[#003366] hover:bg-slate-50"
                        data-testid="export-time-csv-button"
                    >
                        <FileCsv size={16} className="mr-1.5" /> CSV
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => download("xlsx")}
                        className="h-10 border-[#003366] text-[#003366] hover:bg-slate-50"
                        data-testid="export-time-excel-button"
                    >
                        <FileXls size={16} className="mr-1.5" /> Excel
                    </Button>
                    <Button
                        onClick={() => { setNewForm(EMPTY_NEW); setNewSubs([]); setCreateOpen(true); }}
                        className="h-10 bg-[#0066D9] hover:bg-[#0052ad] text-white"
                        data-testid="create-time-record-button"
                    >
                        <Plus size={16} className="mr-1.5" /> New record
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card className="border-[#E2E8F0]" data-testid="time-filters-card">
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">From</Label>
                        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                            className="h-10" data-testid="filter-date-from" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">To</Label>
                        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                            className="h-10" data-testid="filter-date-to" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Employee</Label>
                        <Select value={techFilter} onValueChange={setTechFilter}>
                            <SelectTrigger className="h-10" data-testid="filter-technician"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All employees</SelectItem>
                                {techs.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Site</Label>
                        <Select value={siteFilter} onValueChange={setSiteFilter}>
                            <SelectTrigger className="h-10" data-testid="filter-site"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All sites</SelectItem>
                                {sites.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Data Hall</Label>
                        <Select value={subFilter} onValueChange={setSubFilter} disabled={siteFilter === ALL}>
                            <SelectTrigger className="h-10" data-testid="filter-sublocation"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All</SelectItem>
                                {filterSubs.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</Label>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="h-10" data-testid="filter-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All</SelectItem>
                                <SelectItem value="active">Active (open)</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {hasFilters && (
                        <div className="lg:col-span-6">
                            <Button variant="ghost" onClick={clearFilters} className="text-slate-500 h-8 px-2"
                                data-testid="clear-time-filters">
                                Clear all filters
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]" data-testid="time-records-card">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-10 text-center text-slate-500">Loading time records…</div>
                    ) : entries.length === 0 ? (
                        <div className="p-10 sm:p-16 text-center" data-testid="time-records-empty">
                            <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                <Clock size={26} className="text-slate-400" />
                            </div>
                            <h3 className="font-semibold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                                No time records
                            </h3>
                            <p className="text-sm text-slate-500 mt-1.5">
                                Records appear here as soon as employees clock in.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead>Date</TableHead>
                                        <TableHead>Employee</TableHead>
                                        <TableHead>Site</TableHead>
                                        <TableHead>Data Hall</TableHead>
                                        <TableHead className="hidden lg:table-cell">Job Code</TableHead>
                                        <TableHead>Clock In</TableHead>
                                        <TableHead>Clock Out</TableHead>
                                        <TableHead>Elapsed</TableHead>
                                        <TableHead>Location</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {entries.map((e) => (
                                        <TableRow key={e.id} data-testid={`time-record-row-${e.id}`}>
                                            <TableCell className="whitespace-nowrap text-sm">{formatWorkDate(e.work_date)}</TableCell>
                                            <TableCell className="font-semibold text-[#003366] text-sm">{e.technician_name}</TableCell>
                                            <TableCell className="text-sm">{e.site_name}</TableCell>
                                            <TableCell className="text-sm">
                                                {e.sublocation_name || <span className="text-slate-400">—</span>}
                                            </TableCell>
                                            <TableCell className="hidden lg:table-cell text-xs font-mono text-slate-600">
                                                {e.job_code || <span className="text-slate-400">—</span>}
                                            </TableCell>
                                            <TableCell className="text-sm font-mono whitespace-nowrap">{formatClockTime(e.clock_in_local)}</TableCell>
                                            <TableCell className="text-sm font-mono whitespace-nowrap">
                                                {e.clock_out_local ? formatClockTime(e.clock_out_local) : <span className="text-slate-400">—</span>}
                                            </TableCell>
                                            <TableCell className="text-sm">{e.elapsed_display || <span className="text-slate-400">—</span>}</TableCell>
                                            <TableCell><LocationCell entry={e} /></TableCell>
                                            <TableCell>
                                                {e.status === "active" ? (
                                                    e.needs_review ? (
                                                        <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] uppercase tracking-wider whitespace-nowrap"
                                                            data-testid={`needs-review-badge-${e.id}`}>
                                                            <Warning size={11} weight="fill" className="mr-1" />
                                                            Open {formatSeconds(e.open_seconds)} — Needs Review
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="bg-[#0066D9] text-white text-[10px] uppercase tracking-wider">Active</Badge>
                                                    )
                                                ) : (
                                                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] uppercase tracking-wider">Completed</Badge>
                                                )}
                                                {e.audit_trail?.[0]?.field === "record" ? (
                                                    <Badge className="ml-1 bg-slate-200 text-slate-700 border border-slate-300 text-[10px] uppercase tracking-wider">Admin-created</Badge>
                                                ) : null}
                                                {e.edited_by_admin && e.audit_trail?.some((a) => a.field !== "record") && (
                                                    <Badge className="ml-1 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-wider">Edited</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {e.status === "active" && (
                                                        <Button size="sm" variant="ghost"
                                                            className="h-8 px-2 text-[#0066D9] hover:bg-[#0066D9]/10"
                                                            onClick={() => setClosingEntry(e)}
                                                            title="Close this shift"
                                                            data-testid={`close-shift-${e.id}`}>
                                                            <LockKey size={14} />
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-500"
                                                        onClick={() => setDetailEntry(e)}
                                                        title="View details & GPS"
                                                        data-testid={`view-time-record-${e.id}`}>
                                                        <Eye size={14} />
                                                    </Button>
                                                    {(e.audit_trail?.length || 0) > 0 && (
                                                        <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-500"
                                                            onClick={() => setAuditEntry(e)}
                                                            title="View audit trail"
                                                            data-testid={`audit-time-record-${e.id}`}>
                                                            <ClockCounterClockwise size={14} />
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant="ghost" className="h-8 px-2"
                                                        onClick={() => openEdit(e)}
                                                        data-testid={`edit-time-record-${e.id}`}>
                                                        <PencilSimple size={14} />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Edit / correct */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-time-record-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            Correct time record
                        </DialogTitle>
                        <DialogDescription>
                            {editing?.technician_name} · {formatWorkDate(editing?.work_date)} — every change is
                            logged with your name in the audit trail.
                        </DialogDescription>
                    </DialogHeader>
                    {editForm && (
                        <div className="space-y-4 pt-1">
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Site</Label>
                                <Select
                                    value={editForm.site_id}
                                    onValueChange={async (v) => {
                                        setEditForm((f) => ({ ...f, site_id: v, sublocation_id: "" }));
                                        await loadSubs(v, setEditSubs);
                                    }}
                                >
                                    <SelectTrigger className="h-11" data-testid="edit-site-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            {editSubs.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                        Data Hall / Sub-location
                                        {siteById(editForm.site_id)?.requires_sublocation && <span className="text-[#E6002C]"> *</span>}
                                    </Label>
                                    <Select
                                        value={editForm.sublocation_id || "none"}
                                        onValueChange={(v) => setEditForm((f) => ({ ...f, sublocation_id: v === "none" ? "" : v }))}
                                    >
                                        <SelectTrigger className="h-11" data-testid="edit-sublocation-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">— none —</SelectItem>
                                            {editSubs.map((s) => (
                                                <SelectItem key={s.id} value={s.id}>
                                                    {s.name}{s.job_code ? ` · ${s.job_code}` : ""}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Clock In ({COMPANY_TZ_LABEL})</Label>
                                    <Input type="datetime-local" value={editForm.clock_in_local}
                                        onChange={(e) => setEditForm((f) => ({ ...f, clock_in_local: e.target.value }))}
                                        className="h-11" data-testid="edit-clock-in-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Clock Out ({COMPANY_TZ_LABEL})</Label>
                                    <Input type="datetime-local" value={editForm.clock_out_local}
                                        onChange={(e) => setEditForm((f) => ({ ...f, clock_out_local: e.target.value }))}
                                        className="h-11" data-testid="edit-clock-out-input" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Admin notes</Label>
                                <Textarea value={editForm.notes}
                                    onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                                    className="min-h-[70px]" data-testid="edit-notes-input" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Reason for correction <span className="text-[#E6002C]">*</span></Label>
                                <Input value={editForm.reason}
                                    onChange={(e) => setEditForm((f) => ({ ...f, reason: e.target.value }))}
                                    placeholder="e.g. Employee forgot to clock out"
                                    className="h-11" data-testid="edit-reason-input" />
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-cancel-button">Cancel</Button>
                        <Button onClick={saveEdit} disabled={saving}
                            className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                            data-testid="edit-save-button">
                            {saving ? "Saving…" : "Save correction"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* New record */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-time-record-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            New time record
                        </DialogTitle>
                        <DialogDescription>
                            For employees who forgot to clock in entirely. Marked as admin-created in the audit trail.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-1">
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Employee <span className="text-[#E6002C]">*</span>
                            </Label>
                            <Select value={newForm.technician_id}
                                onValueChange={(v) => setNewForm((f) => ({ ...f, technician_id: v }))}>
                                <SelectTrigger className="h-11" data-testid="new-technician-select">
                                    <SelectValue placeholder="Select employee…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Site <span className="text-[#E6002C]">*</span>
                            </Label>
                            <Select value={newForm.site_id}
                                onValueChange={async (v) => {
                                    setNewForm((f) => ({ ...f, site_id: v, sublocation_id: "" }));
                                    await loadSubs(v, setNewSubs);
                                }}>
                                <SelectTrigger className="h-11" data-testid="new-site-select">
                                    <SelectValue placeholder="Select site…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {newSubs.length > 0 && (
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Data Hall / Sub-location
                                    {siteById(newForm.site_id)?.requires_sublocation && <span className="text-[#E6002C]"> *</span>}
                                </Label>
                                <Select value={newForm.sublocation_id || "none"}
                                    onValueChange={(v) => setNewForm((f) => ({ ...f, sublocation_id: v === "none" ? "" : v }))}>
                                    <SelectTrigger className="h-11" data-testid="new-sublocation-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— none —</SelectItem>
                                        {newSubs.map((s) => (
                                            <SelectItem key={s.id} value={s.id}>
                                                {s.name}{s.job_code ? ` · ${s.job_code}` : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Clock In ({COMPANY_TZ_LABEL}) <span className="text-[#E6002C]">*</span>
                                </Label>
                                <Input type="datetime-local" value={newForm.clock_in_local}
                                    onChange={(e) => setNewForm((f) => ({ ...f, clock_in_local: e.target.value }))}
                                    className="h-11" data-testid="new-clock-in-input" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Clock Out ({COMPANY_TZ_LABEL})</Label>
                                <Input type="datetime-local" value={newForm.clock_out_local}
                                    onChange={(e) => setNewForm((f) => ({ ...f, clock_out_local: e.target.value }))}
                                    className="h-11" data-testid="new-clock-out-input" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Reason</Label>
                            <Input value={newForm.reason}
                                onChange={(e) => setNewForm((f) => ({ ...f, reason: e.target.value }))}
                                placeholder="e.g. Employee forgot to clock in"
                                className="h-11" data-testid="new-reason-input" />
                        </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="new-cancel-button">Cancel</Button>
                        <Button onClick={saveNew} disabled={saving}
                            className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                            data-testid="new-save-button">
                            {saving ? "Saving…" : "Create record"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CloseShiftDialog
                entry={closingEntry}
                open={!!closingEntry}
                onOpenChange={(o) => !o && setClosingEntry(null)}
                onClosed={reload}
            />

            {/* Record detail incl. full GPS */}
            <TimeRecordDetailDialog
                entry={detailEntry}
                open={!!detailEntry}
                onOpenChange={(o) => !o && setDetailEntry(null)}
                onEdit={(entry) => { setDetailEntry(null); openEdit(entry); }}
                onCloseShift={(entry) => { setDetailEntry(null); setClosingEntry(entry); }}
            />

            {/* Audit trail */}
            <Dialog open={!!auditEntry} onOpenChange={(o) => !o && setAuditEntry(null)}>
                <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="audit-trail-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            Audit trail
                        </DialogTitle>
                        <DialogDescription>
                            {auditEntry?.technician_name} · {formatWorkDate(auditEntry?.work_date)}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {(auditEntry?.audit_trail || []).map((a, i) => (
                            <div key={`${a.changed_at}-${a.field}`} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`audit-item-${i}`}>
                                <div className="font-semibold text-[#003366]">{a.field}</div>
                                <div className="text-slate-600 mt-1">
                                    <span className="line-through text-slate-400">{formatAuditValue(a.old_value)}</span>
                                    {" → "}
                                    <span className="font-medium">{formatAuditValue(a.new_value)}</span>
                                </div>
                                {a.reason && <div className="text-xs text-slate-500 mt-1">Reason: {a.reason}</div>}
                                <div className="text-xs text-slate-400 mt-1">
                                    {a.admin_name} · {a.changed_at?.slice(0, 16).replace("T", " ")} UTC
                                </div>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
